import React, { useRef } from 'react';
import { Box, Divider, TextField, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

// Tipos de formulário
export type FormType =
  | 'peticao_inicial'      // Nome, Fatos (obrig), Parte contrária, Docs opcionais
  | 'resposta_simples'     // Nome, Docs, Fatos (opcional), Peça a rebater
  | 'resposta_com_tipo';   // Tipo (Breve/Longa), Nome, Docs, Fatos (opcional), Peça a rebater

export interface FormConfig {
  // Identificação
  formType: FormType;
  title: string;
  description: string;

  // Documentos
  suggestedDocs: string | React.ReactNode;

  // Peça a rebater (para resposta_simples e resposta_com_tipo)
  defaultPecaRebater?: string;
  pecaRebaterHelper?: string;

  // Dica final
  infoTip: string;

  // Opções de tipo (apenas para resposta_com_tipo)
  pieceTypes?: Array<{
    value: string;
    label: string;
    description: string;
  }>;

  // Contexto específico para IA (prompts e instruções)
  aiContextPrompt?: string;
}

// Tipos padrão de peça (Breve/Longa)
export const DEFAULT_PIECE_TYPES = [
  {
    value: 'breve',
    label: 'Breve',
    description: 'Recomenda-se para peças curtas que não exijam teses jurídicas.',
  },
  {
    value: 'longa',
    label: 'Longa',
    description: 'Recomenda-se para peças que exijam maior desenvolvimento argumentativo.',
  },
];

interface GenericFormProps extends FormProps {
  config: FormConfig;
}

const GenericFormTemplate: React.FC<GenericFormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
  config,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');
  const [selectedPieceType, setSelectedPieceType] = React.useState(values.tipo_peca || 'breve');

  // Passar aiContextPrompt para os campos dinâmicos quando o config for carregado
  React.useEffect(() => {
    if (config.aiContextPrompt && !values.ai_context_prompt) {
      onChange({
        ...values,
        ai_context_prompt: config.aiContextPrompt,
      });
    }
  }, [config.aiContextPrompt]);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  const isPeticaoInicial = config.formType === 'peticao_inicial';
  const hasTypeSelector = config.formType === 'resposta_com_tipo';
  const pieceTypes = config.pieceTypes || DEFAULT_PIECE_TYPES;

  return (
    <Box>
      <FormHeader
        documentType={documentType}
        customTitle={config.title}
        customDescription={config.description}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Seletor de Tipo (apenas para resposta_com_tipo) */}
        {hasTypeSelector && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Selecione o tipo da peça que deseja: <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <ToggleButtonGroup
              value={selectedPieceType}
              exclusive
              onChange={(_, newValue) => {
                if (newValue) {
                  setSelectedPieceType(newValue);
                  handleFieldChange('tipo_peca', newValue);
                }
              }}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  py: 2,
                  px: 3,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                },
              }}
            >
              {pieceTypes.map((type) => (
                <ToggleButton key={type.value} value={type.value}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {type.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {type.description}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* Fatos do Caso (Obrigatório para petição inicial) */}
        {isPeticaoInicial && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Quais são os fatos do caso? <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              placeholder="Descreva os fatos do caso de forma detalhada..."
              value={values.fatos_caso || ''}
              onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
              inputProps={{ maxLength: 10000 }}
            />
            <Typography
              variant="caption"
              color={(values.fatos_caso || '').length >= 200 ? 'success.main' : 'text.secondary'}
              sx={{ mt: 0.5, display: 'block' }}
            >
              {(values.fatos_caso || '').length} de 10000 caracteres permitidos
              {(values.fatos_caso || '').length < 200 && ` (mínimo: 200)`}
              {(values.fatos_caso || '').length >= 200 && ' ✓'}
            </Typography>
          </Box>
        )}

        {/* Parte Contrária (apenas para petição inicial) */}
        {isPeticaoInicial && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Escreva o nome da parte contrária: <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder="Ex: O réu é Manuel Pereira."
              value={values.parte_contraria || ''}
              onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
            />
          </Box>
        )}

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs={config.suggestedDocs}
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Fatos do Caso (Opcional para respostas) */}
        {!isPeticaoInicial && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Conte mais sobre o caso trazendo a realidade dos fatos, se achar necessário: (Opcional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Descreva os fatos relevantes..."
              value={values.fatos_caso || ''}
              onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
              inputProps={{ maxLength: 6000 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {(values.fatos_caso || '').length} de 6000 caracteres permitidos.
            </Typography>
          </Box>
        )}

        {/* Peça a Rebater (para respostas) */}
        {!isPeticaoInicial && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Indique a peça do processo que deseja rebater ou responder: <Typography component="span" color="error.main">*</Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder={config.defaultPecaRebater ? `Ex: ${config.defaultPecaRebater}` : 'Ex: Petição inicial'}
              value={values.peca_rebater || config.defaultPecaRebater || ''}
              onChange={(e) => handleFieldChange('peca_rebater', e.target.value)}
              helperText={config.pecaRebaterHelper || 'Informe a peça ou decisão que deseja responder'}
            />
          </Box>
        )}

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os fatos do caso que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message={config.infoTip} />
    </Box>
  );
};

export default GenericFormTemplate;

// ============================================
// HELPER: Criar formulário rapidamente
// ============================================

/**
 * Cria um componente de formulário a partir de uma configuração
 * 
 * Exemplo de uso:
 * 
 * const MeuFormulario = createFormFromConfig({
 *   formType: 'resposta_simples',
 *   title: 'Meu Formulário',
 *   description: 'Descrição do formulário',
 *   suggestedDocs: 'Documento X e Documento Y.',
 *   defaultPecaRebater: 'Sentença',
 *   infoTip: 'Dica para o usuário.',
 * });
 */
export const createFormFromConfig = (config: FormConfig): React.FC<FormProps> => {
  const FormComponent: React.FC<FormProps> = (props) => (
    <GenericFormTemplate {...props} config={config} />
  );
  FormComponent.displayName = config.title.replace(/\s+/g, '');
  return FormComponent;
};

